import React, {useCallback, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {ArrowBack} from "@mui/icons-material";
import ImportCsvFile, {ImportCsvFileSteps} from "../../../../../app-specific/import-csv-file";
import ToastService from "../../../../../../../core/services/toast";
import {createUUId} from "../../../../../../../core/services/utils/utils";
import {PartNumberLookupStore} from "../../../../../../views/panel/operator/settings/import/engine/transformers/lookup-stores/part-number-lookup-store";
import IconButton from "../../../../../base/button/icon_button";
import Button from "../../../../../base/button/button";
import LoadingLottieAnimation from "../../../../../app-specific/lottie-animations/loading";

const initialCsvFileState = {
    step: ImportCsvFileSteps.dropzone,
    parsedColumns: [],
    parsedRows: [],
};

const partMatchesIndustry = (partInfo, industryId) => {
    if (partInfo?.industryId == null || industryId == null) {
        return true;
    }
    return String(partInfo.industryId) === String(industryId);
};

const ImportPartsByCsv = ({
                              activeIndustryId,
                              existingPartIds,
                              onPartsImported,
                              onClose,
                          }) => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.manage-assigned-items-add.importCsv'});
    const formId = useMemo(() => createUUId(), []);
    const [importCsvFileState, setImportCsvFileState] = useState(initialCsvFileState);
    const [resolving, setResolving] = useState(false);
    const partNumberLookupRef = useRef(new PartNumberLookupStore());

    const fields = useMemo(() => [
        {
            name: 'partNumber',
            title: t('partNumberFieldTitle'),
            generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
            format: (e) => e?.toString()?.toUpperCase()?.trim(),
        },
    ], [t]);

    /**
     * Parses the dropped file's rows and columns, and moves to the column mapping step.
     */
    const onDropzoneParsed = useCallback(({rows, columns}) => {
        setImportCsvFileState({
            step: ImportCsvFileSteps.columnMapper,
            parsedColumns: columns,
            parsedRows: rows,
        });
    }, []);

    /**
     * Resolves the part numbers in the parsed rows, imports the valid ones that match the industry and are not duplicates, and shows messages for the results.
     */
    const onColumnMapperParsed = useCallback(async (parsedRows) => {
        const validRows = parsedRows.filter((e) => !!e.partNumber);
        if (!validRows.length) {
            ToastService.error({
                title: t('noValidPartNumbersTitle'),
                message: t('noValidPartNumbersMessage'),
            });
            setImportCsvFileState(initialCsvFileState);
            return;
        }

        const partNumbersUpper = [
            ...new Set(validRows.map((e) => e.partNumber?.toUpperCase()?.trim()).filter(Boolean)),
        ];

        setResolving(true);
        const {partIds, partInfo} = await partNumberLookupRef.current.resolve(partNumbersUpper);
        setResolving(false);

        const importedParts = [];
        let skippedInvalid = 0;
        let skippedIndustry = 0;
        let skippedDuplicate = 0;

        for (const partNoUpper of partNumbersUpper) {
            const info = partInfo[partNoUpper];
            const id = partIds[partNoUpper];
            if (!id || !info) {
                skippedInvalid += 1;
                continue;
            }
            if (!partMatchesIndustry(info, activeIndustryId)) {
                skippedIndustry += 1;
                continue;
            }
            if (existingPartIds.includes(id)) {
                skippedDuplicate += 1;
                continue;
            }
            importedParts.push({
                ...info,
                id,
                partNo: info.partNo ?? partNoUpper,
            });
        }

        if (!importedParts.length) {
            ToastService.error({
                title: t('nothingImportedTitle'),
                message: t('nothingImportedMessage'),
            });
            setImportCsvFileState(initialCsvFileState);
            return;
        }

        onPartsImported(importedParts);

        const skippedTotal = skippedInvalid + skippedIndustry + skippedDuplicate;
        if (skippedTotal > 0) {
            ToastService.warning({
                title: t('partialImportTitle'),
                message: t('partialImportMessage', {
                    imported: importedParts.length,
                    skipped: skippedTotal,
                }),
            });
        } else {
            ToastService.success({
                title: t('importSuccessTitle'),
                message: t('importSuccessMessage', {count: importedParts.length}),
            });
        }

        setImportCsvFileState(initialCsvFileState);
        onClose();
    }, [activeIndustryId, existingPartIds, onClose, onPartsImported, t]);

    if (resolving) {
        return <LoadingLottieAnimation width={'80%'} className={'my-3'}/>;
    }

    return (
        <div className={'w-100 mb-3'}>
            <div className={'d-flex align-items-center mb-3'}>
                <IconButton
                    onPressed={() => importCsvFileState.step === ImportCsvFileSteps.columnMapper ? setImportCsvFileState(initialCsvFileState) : onClose()}
                    color={'paper.secondary'}
                    className={'mr-2'}
                >
                    <ArrowBack className={'icon normal'}/>
                </IconButton>
                <p className={'text-md black font-weight-semi-bold mb-0'}>
                    {importCsvFileState.step !== ImportCsvFileSteps.columnMapper ? t('importCsvButton') : t('columnMappingTitle')}
                </p>
            </div>
            <ImportCsvFile
                step={importCsvFileState.step}
                formId={formId}
                fields={fields}
                parsedColumns={importCsvFileState.parsedColumns}
                parsedRows={importCsvFileState.parsedRows}
                onDropzoneParsed={onDropzoneParsed}
                onColumnMapperParsed={onColumnMapperParsed}
                sampleFileTitle={'assigned-items-part-numbers-sample.csv'}
                dropzoneNotes={
                    <p className={'placeholder-text text-center mb-0'}>
                        {t('dropzoneNotes')}
                    </p>
                }
            />
            {
                importCsvFileState.step === ImportCsvFileSteps.columnMapper &&
                <div className={'d-flex justify-content-end mt-5'}>
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        size={'small'}
                        className={'mr-2'}
                        onClick={() => setImportCsvFileState(initialCsvFileState)}
                    >
                        {t('backButton')}
                    </Button>
                    <Button
                        variant={'contained'}
                        color={'primary'}
                        size={'small'}
                        type={'submit'}
                        form={formId}
                    >
                        {t('proceedButton')}
                    </Button>
                </div>
            }
        </div>
    );
};

export default ImportPartsByCsv;
